import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, money, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { DatePicker } from "@/components/DatePicker";
import { Briefcase, Plus, Trash } from "@phosphor-icons/react";
import { format } from "date-fns";
import { toast } from "sonner";

const empty = { client: "", entry_date: format(new Date(), "yyyy-MM-dd"), start_time: "18:00", end_time: "22:00", hourly_rate: "" };

export default function PaidDuty() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const currency = user?.currency || "CAD";

  const { data: entries = [] } = useQuery({
    queryKey: ["paid-duty"],
    queryFn: async () => (await api.get("/paid-duty")).data,
  });

  const createMut = useMutation({
    mutationFn: async (payload) => (await api.post("/paid-duty", payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["paid-duty"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["projections"] });
      toast.success("Paid duty entry added.");
      setForm(empty);
      setOpen(false);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => api.delete(`/paid-duty/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["paid-duty"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["projections"] });
      toast.success("Entry removed.");
    },
  });

  const submit = (e) => {
    e.preventDefault();
    createMut.mutate({ ...form, hourly_rate: parseFloat(form.hourly_rate) || 0 });
  };

  const total = entries.reduce((s, e) => s + (e.amount || 0), 0);

  return (
    <div className="animate-fade-up" data-testid="paid-duty-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <p className="overline text-emerald-400">Module</p>
          <h1 className="text-3xl font-black tracking-tight mt-1">Paid Duty</h1>
          <p className="text-muted-foreground text-sm mt-1">Third-party engagements billed at a client rate.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-paid-duty-button" className="rounded-md font-semibold">
              <Plus className="h-4 w-4 mr-1.5" weight="bold" /> Add Entry
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-popover border-border" data-testid="paid-duty-dialog">
            <DialogHeader><DialogTitle>New Paid Duty Entry</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label>Client</Label>
                <Input data-testid="paid-duty-client" className="mt-1.5 bg-secondary/50" required
                  value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })}
                  placeholder="City Events Corp" />
              </div>
              <div>
                <Label>Date</Label>
                <div className="mt-1.5"><DatePicker testId="paid-duty-date" value={form.entry_date}
                  onChange={(d) => setForm({ ...form, entry_date: d })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Start Time</Label>
                  <Input type="time" data-testid="paid-duty-start" className="mt-1.5 bg-secondary/50" required
                    value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
                </div>
                <div>
                  <Label>End Time</Label>
                  <Input type="time" data-testid="paid-duty-end" className="mt-1.5 bg-secondary/50" required
                    value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Hourly Rate ({currency})</Label>
                <Input type="number" step="0.01" min="0" data-testid="paid-duty-rate" className="mt-1.5 bg-secondary/50" required
                  value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })}
                  placeholder="65.00" />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={createMut.isPending} data-testid="paid-duty-submit" className="rounded-md font-semibold">
                  {createMut.isPending ? "Saving…" : "Save Entry"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {entries.length === 0 ? (
        <Card className="p-12 bg-card border-border text-center" data-testid="paid-duty-empty">
          <Briefcase className="h-10 w-10 text-muted-foreground mx-auto mb-3" weight="bold" />
          <p className="text-muted-foreground">No paid duty entries yet. Add your first one.</p>
        </Card>
      ) : (
        <Card className="bg-card border-border overflow-hidden">
          <div className="hidden sm:grid grid-cols-12 gap-2 px-5 py-3 border-b border-border overline text-muted-foreground">
            <span className="col-span-4">Client</span><span className="col-span-2">Date</span>
            <span className="col-span-2">Hours</span><span className="col-span-2">Rate</span>
            <span className="col-span-2 text-right">Amount</span>
          </div>
          {entries.map((e) => (
            <div key={e.id} data-testid={`paid-duty-row-${e.id}`}
              className="grid grid-cols-2 sm:grid-cols-12 gap-2 px-5 py-4 border-b border-border last:border-0 items-center hover:bg-secondary/30 transition-colors duration-200">
              <span className="col-span-2 sm:col-span-4 font-medium">{e.client}
                <span className="block text-xs text-muted-foreground sm:hidden">{e.entry_date} · {e.hours}h</span>
              </span>
              <span className="hidden sm:block col-span-2 text-muted-foreground">{format(new Date(e.entry_date + "T00:00:00"), "MMM d")}</span>
              <span className="hidden sm:block col-span-2 text-muted-foreground">{e.hours}h ({e.start_time}–{e.end_time})</span>
              <span className="hidden sm:block col-span-2 text-muted-foreground">{money(e.hourly_rate, currency)}</span>
              <div className="col-span-2 flex items-center justify-end gap-3">
                <span className="font-bold text-emerald-400">{money(e.amount, currency)}</span>
                <button data-testid={`delete-paid-duty-${e.id}`} onClick={() => deleteMut.mutate(e.id)}
                  className="text-muted-foreground hover:text-destructive transition-colors duration-200">
                  <Trash className="h-4 w-4" weight="bold" />
                </button>
              </div>
            </div>
          ))}
          <div className="flex justify-between px-5 py-4 bg-secondary/30">
            <span className="font-semibold">Total Paid Duty</span>
            <span className="font-black text-emerald-400" data-testid="paid-duty-total">{money(total, currency)}</span>
          </div>
        </Card>
      )}
    </div>
  );
}
